import { createSlice } from '@reduxjs/toolkit';
import { confirmOTPCode, getUser, requestOTPCode } from './actions';
import { UserData } from '@/types/user';

export type StateUserProps = {
  user: UserData | null;
  error: Record<string, string> | string | null;
  isUserFromMobile: boolean;
  isCodeRequested: boolean;
  isLoading: boolean;
  isLoggedIn: boolean;
  isAccountCreating: boolean;
  isOnboardingSurveyCompleted: boolean;
};

const initialState: StateUserProps = {
  user: null,
  error: null,
  isUserFromMobile: false,
  isCodeRequested: false,
  isLoading: false,
  isLoggedIn: false,
  isAccountCreating: false,
  isOnboardingSurveyCompleted: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setIsLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setIsCodeRequested: (state, action) => {
      state.isCodeRequested = action.payload;
    },
    setIsOnboardingSurveyCompleted: (state, action) => {
      state.isOnboardingSurveyCompleted = action.payload;
    },
    setUserFromMobile: (state, action) => {
      state.isUserFromMobile = action.payload;
    },
    clearStore: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoading = false;
      state.isLoggedIn = true;
    });
    builder.addCase(getUser.rejected, (state) => {
      state.isLoading = false;
    });

    builder.addCase(requestOTPCode.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(requestOTPCode.fulfilled, (state) => {
      state.isLoading = false;
      state.isCodeRequested = true;
    });
    builder.addCase(requestOTPCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as StateUserProps['error'];
    });

    builder.addCase(confirmOTPCode.pending, (state) => {
      state.isAccountCreating = true;
    });
    builder.addCase(confirmOTPCode.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isAccountCreating = false;
      state.isLoggedIn = true;
    });
    builder.addCase(confirmOTPCode.rejected, (state, action) => {
      state.isAccountCreating = false;
      state.error = action.payload as StateUserProps['error'];
    });
  },
  selectors: {
    selectUser: (state) => state.user,
    selectIsLoading: (state) => state.isLoading,
    selectIsLoggedIn: (state) => state.isLoggedIn,
    selectIsUserFromMobile: (state) => state.isUserFromMobile,
    selectIsAccountCreating: (state) => state.isAccountCreating,
    selectIsCodeRequested: (state) => state.isCodeRequested,
    selectError: (state) => state.error,
    selectIsOnboardingSurveyCompleted: (state) => state.isOnboardingSurveyCompleted,
  },
});

export const {
  clearStore,
  setUser,
  setIsCodeRequested,
  setError,
  setIsOnboardingSurveyCompleted,
  setUserFromMobile,
} = userSlice.actions;

export const {
  selectUser,
  selectIsLoading,
  selectIsLoggedIn,
  selectIsAccountCreating,
  selectError,
  selectIsCodeRequested,
  selectIsOnboardingSurveyCompleted,
  selectIsUserFromMobile,
} = userSlice.selectors;
